import { formatDate } from '@/utils'
import type { MonthKey } from './types'

const MONTHS_PER_YEAR = 12

const toIndex = (month: MonthKey): number => {
  const [year, monthNumber] = month.split('-').map(Number)
  return year * MONTHS_PER_YEAR + (monthNumber - 1)
}

const fromIndex = (index: number): MonthKey => {
  const year = Math.floor(index / MONTHS_PER_YEAR)
  const monthNumber = (index % MONTHS_PER_YEAR) + 1
  return `${year}-${String(monthNumber).padStart(2, '0')}`
}

export const monthOf = (calendarDate: string): MonthKey =>
  calendarDate.slice(0, 7)

export const nextMonth = (month: MonthKey): MonthKey =>
  fromIndex(toIndex(month) + 1)

export const monthsEarlier = (month: MonthKey, count: number): MonthKey =>
  fromIndex(toIndex(month) - count)

// Jan to Apr is 3.
export const monthsApart = (first: MonthKey, last: MonthKey): number =>
  toIndex(last) - toIndex(first)

// Every month from first to last, both included. Empty when last comes before first.
export const monthsBetween = (first: MonthKey, last: MonthKey): MonthKey[] => {
  const months: MonthKey[] = []
  for (let index = toIndex(first); index <= toIndex(last); index++) {
    months.push(fromIndex(index))
  }
  return months
}

// The months a statement's printed dates span. With no start date, only the month it ends in.
export const printedMonthsOf = (
  startDate: string | null,
  endDate: string,
): MonthKey[] => monthsBetween(monthOf(startDate ?? endDate), monthOf(endDate))

// Months next to each other fold into one run: [Mar, Apr, Jun] gives [[Mar, Apr], [Jun]].
export const consecutiveRuns = (months: MonthKey[]): MonthKey[][] => {
  const runs: MonthKey[][] = []
  for (const month of [...months].sort()) {
    const run = runs[runs.length - 1]
    const previous = run?.[run.length - 1]
    if (previous && monthsBetween(previous, month).length === 2) run.push(month)
    else runs.push([month])
  }
  return runs
}

// "Nov 2022"
export const monthLabel = (month: MonthKey): string =>
  formatDate(`${month}-01`, 'MMM yyyy')

// "Nov"
export const monthShortLabel = (month: MonthKey): string =>
  formatDate(`${month}-01`, 'MMM')

const pad = (value: number) => String(value).padStart(2, '0')

export const dayAfter = (calendarDate: string): string => {
  const [year, month, day] = calendarDate.split('-').map(Number)
  const next = new Date(year, month - 1, day + 1)
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
}

const MS_PER_DAY = 86_400_000

const dayNumber = (calendarDate: string): number => {
  const [year, month, day] = calendarDate.split('-').map(Number)
  return Date.UTC(year, month - 1, day) / MS_PER_DAY
}

export const daysApart = (first: string, second: string): number =>
  Math.abs(dayNumber(first) - dayNumber(second))
